import React from 'react';
import { AlertCircleIcon, CheckIcon, ChevronDownIcon } from 'lucide-react';
import { PhoneField } from './PhoneField';
import { PasswordField } from './PasswordField';
import { inputBase, inputBorder } from './fieldStyles';
import type { FormField, FormValues } from '../../types/form';
type FormFieldControlProps = {
  field: FormField;
  idPrefix: string;
  values: FormValues;
  required: boolean;
  error?: string;
  onChange: (name: string, value: string | boolean) => void;
  onBlur: (name: string) => void;
};
export function FormFieldControl({
  field,
  idPrefix,
  values,
  required,
  error,
  onChange,
  onBlur
}: FormFieldControlProps) {
  const id = `${idPrefix}-${field.name}`;
  const errorId = `${id}-error`;
  const helperId = `${id}-helper`;
  const hasError = Boolean(error);
  const describedBy = [field.helper ? helperId : '', hasError ? errorId : ''].filter(Boolean).join(' ') || undefined;
  const value = typeof values[field.name] === 'string' ? values[field.name] as string : '';
  const span = field.half ? 'sm:col-span-1' : 'sm:col-span-2';
  const labelEl = <span className="flex items-baseline gap-1.5 text-[14px] font-medium text-ink" data-id="element-447" data-mp="3h86xba5sezo5">
      {field.label}
      {required ? <span className="text-brand" aria-hidden="true" data-id="element-448" data-mp="389v9ffwb2fze">
          *
        </span> : <span className="text-[13px] font-normal text-ink-muted" data-id="element-449" data-mp="3d86hbl31igez">(optional)</span>}
    </span>;
  const messages = <>
      {field.helper && !hasError && <p id={helperId} className="mt-1.5 text-[13px] text-ink-muted" data-id="element-450" data-mp="4qs87m5yfw2p">
          {field.helper}
        </p>}
      {hasError && <p id={errorId} className="mt-1.5 flex items-center gap-1.5 text-[13px] font-medium text-[#C0362C]" data-id="element-451" data-mp="3r0mfa7h33o91">
          <AlertCircleIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-452" data-mp="3vxuz0chvs0ce" />
          {error}
        </p>}
    </>;
  if (field.type === 'checkbox') {
    const checked = values[field.name] === true;
    return <div className="sm:col-span-2" data-id="element-453" data-mp="2kl29l8ckbnzi">
        <label htmlFor={id} className="flex cursor-pointer items-start gap-3" data-id="element-454" data-mp="20heibxbwsmb8">
          <span className="relative mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center" data-id="element-455" data-mp="20jh6ebti45db">
            <input id={id} type="checkbox" checked={checked} required={required} aria-invalid={hasError} aria-describedby={describedBy} onChange={e => onChange(field.name, e.target.checked)} className={`peer h-5 w-5 cursor-pointer appearance-none rounded-[5px] border bg-white transition-colors duration-150 ease-out checked:border-brand checked:bg-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30 ${hasError ? 'border-[#C0362C]' : 'border-ink/25'}`} data-id="element-456" data-mp="1wi5m8lteccci" />
            <CheckIcon className="pointer-events-none absolute h-3.5 w-3.5 text-white opacity-0 peer-checked:opacity-100" strokeWidth={3} aria-hidden="true" data-id="element-457" data-mp="334rg165sc3xn" />
          </span>
          <span className="text-[14px] leading-[1.5] text-ink-soft" data-id="element-458" data-mp="35oeckr2dkoef">
            {field.label}
            {required && <span className="ml-1 text-brand" aria-hidden="true" data-id="element-459" data-mp="3alxsrwimvb90">
                *
              </span>}
          </span>
        </label>
        {messages}
      </div>;
  }
  if (field.type === 'choice') {
    return <fieldset className={span} aria-describedby={describedBy} data-id="element-460" data-mp="2mqlcdwj4yskq">
        <legend className="mb-2" data-id="element-461" data-mp="2msej1btr1csq">{labelEl}</legend>
        <div className="grid gap-2" style={{
        gridTemplateColumns: `repeat(${field.options.length}, minmax(0, 1fr))`
      }} data-id="element-462" data-mp="2rq7lbtrxlmnq">
          {field.options.map((option, index) => {
          const checked = value === option;
          return <label key={option} className={`relative flex h-12 cursor-pointer items-center justify-center rounded-[10px] border px-2 text-center text-[15px] font-medium transition-colors duration-150 ease-out focus-within:ring-2 focus-within:ring-brand/20 ${checked ? 'border-brand bg-brand-soft text-ink' : hasError ? 'border-[#C0362C] bg-white text-ink' : 'border-ink/15 bg-white text-ink hover:border-ink/30'}`} data-id="element-463" data-mp="30nqe5kbth5a6" data-mpi={index}>
                <input id={index === 0 ? id : undefined} type="radio" name={id} value={option} checked={checked} required={required} onChange={() => onChange(field.name, option)} className="sr-only" data-id="element-464" data-mp="jg86qbybis0v" />
                <span className="whitespace-nowrap" data-id="element-465" data-mp="127rawm42wc4h">{option}</span>
              </label>;
        })}
        </div>
        {messages}
      </fieldset>;
  }
  let control: React.ReactNode;
  const common = {
    id,
    required,
    'aria-required': required,
    'aria-invalid': hasError,
    'aria-describedby': describedBy,
    onBlur: () => onBlur(field.name)
  };
  switch (field.type) {
    case 'tel':
      control = <PhoneField id={id} value={value} country={values[`${field.name}__cc`] as string || 'RW'} hasError={hasError} required={required} describedBy={describedBy} onChange={v => onChange(field.name, v)} onCountryChange={c => onChange(`${field.name}__cc`, c)} onBlur={() => onBlur(field.name)} data-id="element-466" data-mp="1zrhnkw21fvpw" />;
      break;
    case 'password':
      control = <PasswordField id={id} value={value} hasError={hasError} describedBy={describedBy} onChange={v => onChange(field.name, v)} onBlur={() => onBlur(field.name)} data-id="element-467" data-mp="134cqi3mxb7ol" />;
      break;
    case 'select':
      control = <div className="relative" data-id="element-468" data-mp="y4i1k5mpks66">
          <select {...common} value={value} onChange={e => onChange(field.name, e.target.value)} className={`${inputBase} ${inputBorder(hasError)} cursor-pointer appearance-none pr-11 ${value ? 'text-ink' : 'text-ink-muted/80'}`} data-id="element-469" data-mp="y3glfmqu5fhl">
            <option value="" disabled data-id="element-470" data-mp="2qypw12vqxd4j">
              {field.placeholder ?? 'Select'}
            </option>
            {field.options.map(o => <option key={o} value={o} className="text-ink" data-id="element-471" data-mp="3ir8ku102sopz">
                {o}
              </option>)}
          </select>
          <ChevronDownIcon className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" aria-hidden="true" data-id="element-472" data-mp="3eok83i88qxnq" />
        </div>;
      break;
    case 'textarea':
      control = <textarea {...common} rows={field.rows ?? 4} placeholder={field.placeholder} value={value} onChange={e => onChange(field.name, e.target.value)} className={`${inputBase} ${inputBorder(hasError)} h-auto min-h-[96px] resize-y py-3 leading-[1.5]`} data-id="element-473" data-mp="1ycoo5nkkjxh4" />;
      break;
    case 'email':
      control = <input {...common} type="email" inputMode="email" autoComplete="email" autoCapitalize="none" spellCheck={false} placeholder={field.placeholder} value={value} onChange={e => onChange(field.name, e.target.value)} className={`${inputBase} ${inputBorder(hasError)}`} data-id="element-474" data-mp="1t7unvaae48pa" />;
      break;
    default:
      control = <input {...common} type="text" autoComplete={field.autoComplete} placeholder={field.placeholder} value={value} onChange={e => onChange(field.name, e.target.value)} className={`${inputBase} ${inputBorder(hasError)}`} data-id="element-475" data-mp="2bzdrnjc8nqsd" />;
  }
  return <div className={span} data-id="element-476" data-mp="mje8raitimzj">
      <label htmlFor={id} className="mb-2 block" data-id="element-477" data-mp="2ogoftlb512n3">
        {labelEl}
      </label>
      {control}
      {messages}
    </div>;
}